import { useEffect, useMemo, useState } from "react";
import { Avatar, Spin, message } from "antd";
import { t } from "i18next";
import { map } from "rxjs/operators";
import IconTiktok from "@icons/dark/tiktok.svg?react";
import { ClipDropdown, type ClipDropdownOption } from "@components/ClipDropdown/ClipDropdown";
import { DangerConfirmModal } from "@components/DangerConfirmModal";
import { Tabs, type TabOption } from "@src/ui/Tabs";
import { useAppSelector } from "@hooks/redux";
import { type LinkedAccount, selectTiktokLinkedAccounts, useLinkedAccounts } from "@store/linkedAccountSlice";
import { useScrollLoad } from "@hooks/useScrollLoad";
import {
  cancelPublish,
  type PublishPageResponse,
  queryPublishPage,
  queryPublishPage$,
  UserPublishPageRequest,
} from "@apis/http/api";
import {
  UserPublishResultResponsePublishMode,
  UserPublishResultResponseStatus,
  UserLinkAccountItemResponsePlatform,
} from "@apis/generated/openapi";
import { PostToTikTokModal } from "@layouts/GlobalVideoDetailModal/PostToTikTokModal/PostToTikTokModal";
import { useSerializeCallback } from "@kalodata/tools";
import { Trans } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { PostVideoFloatingBar } from "@components/PostVideoButton/PostVideoFloatingBar";
import { PostVideoModal } from "@components/PostVideoButton/PostVideoModal/PostVideoModal";
import { PublishRecordCard } from "./PublishRecordCard";
import { SocialPublisherEmptyState } from "./SocialPublisherEmptyState";

type PublisherTab = "published" | "scheduled";
type PublishRecord = NonNullable<NonNullable<PublishPageResponse>["data"]>[number];

const PLATFORM_VALUE = UserLinkAccountItemResponsePlatform.TIKTOK;
const ALL_ACCOUNTS_VALUE = "__all_accounts__";
const TAB_QUERY_KEY = "tab";

const parseTabFromSearchParams = (params: URLSearchParams): PublisherTab => {
  const tab = params.get(TAB_QUERY_KEY);
  return tab === "published" || tab === "scheduled" ? tab : "published";
};

const normalizeAccountName = (value?: string | null) => {
  if (!value) return "";
  return value.startsWith("@") ? value : `@${value}`;
};

const isScheduledStatus = (status?: UserPublishResultResponseStatus) =>
  status === UserPublishResultResponseStatus.PENDING_PUBLISH || status === UserPublishResultResponseStatus.FAIL;

const mapToPageResult = (res: PublishPageResponse | undefined, fallbackPageNo: number, fallbackPageSize: number) => ({
  data: res?.data ?? [],
  pageNo: res?.pageNo ?? fallbackPageNo,
  pageSize: res?.pageSize ?? fallbackPageSize,
  total: res?.total ?? 0,
});

interface FilterBarProps {
  activeTab: PublisherTab;
  onTabChange: (tab: PublisherTab) => void;
  scheduledBadge?: number;
  accounts: LinkedAccount[];
  selectedAccountId?: number;
  onSelectAccount: (linkId?: number) => void;
}

const FilterBar = (props: FilterBarProps) => {
  const { activeTab, onTabChange, accounts, selectedAccountId, onSelectAccount } = props;
  const selectedAccount = accounts.find((item) => item.id === selectedAccountId);

  const tabOptions = useMemo<TabOption[]>(
    () => [
      { label: "Published", value: "published" },
      { label: "Scheduled", value: "scheduled" },
    ],
    [],
  );

  const platformOptions: ClipDropdownOption[] = [
    {
      key: PLATFORM_VALUE,
      label: (
        <div className="flex items-center gap-[8px]">
          <div className="flex size-[20px] items-center justify-center rounded-[4px] bg-black">
            <IconTiktok width={16} height={16} />
          </div>
          <span>TikTok</span>
        </div>
      ),
    },
  ];

  const accountOptions: ClipDropdownOption[] = [
    {
      key: ALL_ACCOUNTS_VALUE,
      label: t("All accounts"),
    },
    ...accounts.map((account) => ({
      key: String(account.id ?? ""),
      label: (
        <div className="flex items-center gap-[6px]">
          <Avatar src={account.accountImage || undefined} size={20} className="shrink-0">
            {(account.accountName || "U").slice(0, 1).toUpperCase()}
          </Avatar>
          <div className="max-w-[160px] truncate">{normalizeAccountName(account.accountName)}</div>
        </div>
      ),
    })),
  ];

  return (
    <div className="flex items-center justify-between gap-[16px]">
      <Tabs
        type="btn"
        value={activeTab}
        options={tabOptions}
        onChange={(value) => onTabChange(value as PublisherTab)}
      />

      <div className="flex items-center gap-[8px]">
        <ClipDropdown
          value={PLATFORM_VALUE}
          onChange={() => {}}
          options={platformOptions}
          title={
            <div className="flex items-center gap-[6px]">
              <div className="flex size-[20px] items-center justify-center rounded-[4px] bg-black">
                <IconTiktok width={16} height={16} />
              </div>
              <span>TikTok</span>
            </div>
          }
          overlayClassName="w-[200px]"
        />

        <div className="h-[14px] w-px bg-kds-border" />

        <ClipDropdown
          value={selectedAccount ? String(selectedAccount.id ?? "") : ALL_ACCOUNTS_VALUE}
          onChange={(key) => {
            if (key === ALL_ACCOUNTS_VALUE) {
              onSelectAccount(undefined);
              return;
            }
            onSelectAccount(Number(key));
          }}
          options={accountOptions}
          placeholder={t("All accounts")}
          overlayClassName="w-[200px]"
        />
      </div>
    </div>
  );
};

const SCHEDULED_STATUSES = [UserPublishResultResponseStatus.PENDING_PUBLISH, UserPublishResultResponseStatus.FAIL];

export interface SocialPublisherProps {
  // props
}

export const SocialPublisher = (_props: SocialPublisherProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<PublisherTab>(() => parseTabFromSearchParams(searchParams));
  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>();
  const [scheduledBadge, setScheduledBadge] = useState(0);
  const [unscheduleTarget, setUnscheduleTarget] = useState<PublishRecord | null>(null);
  const [republishTarget, setRepublishTarget] = useState<PublishRecord | null>(null);
  const [postVideoOpen, setPostVideoOpen] = useState(false);

  useLinkedAccounts(true);
  const accounts = useAppSelector(selectTiktokLinkedAccounts);

  const queryParams = useMemo<UserPublishPageRequest>(
    () => ({
      linkId: selectedAccountId,
      statuses: activeTab === "published" ? UserPublishResultResponseStatus.SUCCESS : SCHEDULED_STATUSES.join(","),
      publishMode: activeTab === "scheduled" ? UserPublishResultResponsePublishMode.SCHEDULE : undefined,
    }),
    [activeTab, selectedAccountId],
  );

  const { items, loading, loadingMore, hasMore, sentinelRef, refresh, removeItem, error } = useScrollLoad<
    PublishRecord,
    typeof queryParams
  >({
    params: queryParams,
    pageSize: 20,
    fetcher: (params) =>
      queryPublishPage$(params).pipe(
        map((response) => {
          const pageResult = mapToPageResult(response, params.pageNo, params.pageSize);
          if (activeTab !== "scheduled") {
            return pageResult;
          }
          return {
            ...pageResult,
            data: pageResult.data.filter((item) => isScheduledStatus(item.status)),
          };
        }),
      ),
  });

  useEffect(() => {
    const tabFromUrl = parseTabFromSearchParams(searchParams);
    setActiveTab((prev) => (prev === tabFromUrl ? prev : tabFromUrl));
  }, [searchParams]);

  useEffect(() => {
    const run = async () => {
      const response = await queryPublishPage({
        publishMode: UserPublishResultResponsePublishMode.SCHEDULE,
        pageNo: 1,
        pageSize: 1,
      });
      const total = response?.total ?? 0;
      setScheduledBadge(total > 0 ? total : 0);
    };
    void run();
  }, []);

  const handleConfirmUnschedule = useSerializeCallback(async () => {
    if (!unscheduleTarget?.id) return;
    await cancelPublish({ id: unscheduleTarget.id });
    removeItem((record) => record.id === unscheduleTarget.id);
    setUnscheduleTarget(null);
    message.success(t("Unscheduled successfully"));
    refresh();
  });

  const handleTabChange = (tab: PublisherTab) => {
    setActiveTab(tab);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set(TAB_QUERY_KEY, tab);
    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b border-kds-border px-[16px] py-[15px]">
        <h1 className="text-[16px] font-semibold leading-[22px] text-kds-fg">
          <Trans>Social Publisher</Trans>
        </h1>
      </header>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col gap-[16px] px-[24px] py-[16px]">
          <FilterBar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            scheduledBadge={scheduledBadge}
            accounts={accounts}
            selectedAccountId={selectedAccountId}
            onSelectAccount={setSelectedAccountId}
          />

          {loading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spin />
            </div>
          ) : items.length === 0 ? (
            <SocialPublisherEmptyState tab={activeTab} onPostVideo={() => setPostVideoOpen(true)} />
          ) : (
            <div className="min-h-0 flex-1 overflow-auto pb-[88px] pr-[4px]">
              <div className="grid grid-cols-1 gap-[16px] 2xl:grid-cols-2">
                {items.map((item) => (
                  <PublishRecordCard
                    key={item.id}
                    tab={activeTab}
                    item={item}
                    accounts={accounts}
                    onUnschedule={setUnscheduleTarget}
                    onRepublish={setRepublishTarget}
                  />
                ))}
              </div>

              <div ref={sentinelRef} className="h-[24px]" />

              {loadingMore ? (
                <div className="flex justify-center py-[8px]">
                  <Spin size="small" />
                </div>
              ) : null}

              {!hasMore && items.length > 0 ? (
                <div className="py-[8px] text-center text-[12px] text-kds-fg-soft">
                  <Trans>No more items</Trans>
                </div>
              ) : null}

              {error ? <div className="py-[8px] text-center text-[12px] text-kds-danger">{error.message}</div> : null}
            </div>
          )}
        </div>

        {!loading && items.length > 0 ? <PostVideoFloatingBar onClick={() => setPostVideoOpen(true)} /> : null}
      </div>

      <PostVideoModal open={postVideoOpen} onClose={() => setPostVideoOpen(false)} onPostToTikTokClose={refresh} />

      <DangerConfirmModal
        open={!!unscheduleTarget}
        title={t("Unschedule this post?")}
        description={t("Auto publishing will be removed. This action cannot be undone.")}
        confirmText={t("Unschedule")}
        confirmLoading={handleConfirmUnschedule.isPending()}
        onConfirm={handleConfirmUnschedule}
        onCancel={() => setUnscheduleTarget(null)}
      />

      <PostToTikTokModal
        open={!!republishTarget}
        assetId={republishTarget?.assetId ?? 0}
        preferredLinkId={republishTarget?.linkId}
        onClose={() => {
          setRepublishTarget(null);
          refresh();
        }}
      />
    </div>
  );
};
